import * as THREE from 'three';
import { FeatureFace, FeatureResultMesh, isFeatureFace } from '../../mesh/feature';
import type { SketchPlanarFaceSelection } from './SketchPlanarFaceSelection';

//------ 拓扑平面面判定 开始------
/**
 * 拓扑平面判定结果。
 *
 * 设计说明：
 * 1. 草图入口需要区分“拓扑明确平面”“拓扑明确非平面”和“旧数据无拓扑信息”三类结果；
 * 2. 使用枚举承载有限状态，避免用 `boolean | null` 在调用链中隐式表达业务语义；
 * 3. `Unknown` 只用于兼容旧数据，不能覆盖拓扑已经明确给出的曲面类型。
 */
enum SketchTopologyPlanarity {
    /** 拓扑明确表示当前面是平面面。 */
    Planar = 'planar',
    /** 拓扑明确表示当前面不是平面面。 */
    NonPlanar = 'non-planar',
    /** 拓扑快照缺少曲面类型，需要回退到旧网格法向判断。 */
    Unknown = 'unknown',
}
//------ 拓扑平面面判定 结束------

/**
 * 平面面选择解析器。
 *
 * 设计说明：
 * 1. 该解析器负责把当前选中的 `FeatureFace` 收口为稳定的草图宿主快照；
 * 2. 平面性判断优先依赖拓扑曲面类型，法向/代表点求解必须集中在这里，避免宿主层重复推导；
 * 3. 返回值允许 `isPlanar=false`，以便上层统一提示而不是吞掉非法选择。
 */
export class SketchPlanarFaceResolver {
    /** 面法向一致性阈值。 */
    private static readonly PLANAR_DOT_THRESHOLD = 0.995;

    /**
     * 解析当前选择对象。
     *
     * @param object 当前主选对象。
     * @returns 平面面快照；无法识别时返回 null。
     */
    public resolveFromSelectionObject(object: THREE.Object3D | null): SketchPlanarFaceSelection | null {
        if (!object || !isFeatureFace(object)) {
            return null;
        }

        const owner = object.parent instanceof FeatureResultMesh
            ? object.parent
            : object.getOwningResultMesh();
        if (!(owner instanceof FeatureResultMesh)) {
            return null;
        }

        owner.updateMatrixWorld(true);
        object.updateMatrixWorld(true);

        const snapshotFace = owner.getTopologySnapshot()?.faces.find((face) => face.index === object.faceIndex) ?? null;
        const worldVertices = this.collectWorldVertices(object);
        const worldCentroid = this.buildWorldCentroid(worldVertices);
        const worldRepresentativePoint = this.buildWorldRepresentativePoint(owner, snapshotFace, worldCentroid);
        const worldPlaneNormal = this.buildWorldPlaneNormal(owner, object, snapshotFace);
        //------ 拓扑平面面判定 开始------
        const isPlanar = this.isPlanarFace(object, snapshotFace);
        //------ 拓扑平面面判定 结束------

        return {
            id: `${object.objectId}:face:${object.faceIndex}`,
            objectId: object.objectId,
            faceIndex: object.faceIndex,
            isPlanar,
            worldPlaneNormal,
            worldPlanePoint: worldRepresentativePoint.clone(),
            worldCentroid,
            worldRepresentativePoint,
            worldVertices,
        };
    }

    /**
     * 收集面的世界坐标顶点。
     *
     * @param face 目标面代理。
     * @returns 去重后的世界坐标顶点。
     */
    private collectWorldVertices(face: FeatureFace): THREE.Vector3[] {
        const geometry = face.geometry;
        const positionAttribute = geometry.getAttribute('position');
        if (!(positionAttribute instanceof THREE.BufferAttribute)) {
            return [];
        }

        const indexAttribute = geometry.getIndex();
        const uniqueVertexIndices = new Set<number>();
        if (indexAttribute instanceof THREE.BufferAttribute) {
            for (let index = 0; index < indexAttribute.count; index += 1) {
                uniqueVertexIndices.add(indexAttribute.getX(index));
            }
        } else {
            for (let index = 0; index < positionAttribute.count; index += 1) {
                uniqueVertexIndices.add(index);
            }
        }

        return [...uniqueVertexIndices].map((rawVertexIndex) => new THREE.Vector3(
            positionAttribute.getX(rawVertexIndex),
            positionAttribute.getY(rawVertexIndex),
            positionAttribute.getZ(rawVertexIndex),
        ).applyMatrix4(face.matrixWorld));
    }

    /**
     * 构造世界坐标质心。
     *
     * @param worldVertices 世界坐标顶点集合。
     * @returns 质心。
     */
    private buildWorldCentroid(worldVertices: THREE.Vector3[]): THREE.Vector3 {
        if (worldVertices.length <= 0) {
            return new THREE.Vector3(0, 0, 0);
        }

        return worldVertices
            .reduce(
                (accumulator, vertex) => accumulator.add(vertex),
                new THREE.Vector3(),
            )
            .multiplyScalar(1 / worldVertices.length);
    }

    /**
     * 构造世界坐标代表点。
     *
     * @param owner 所属结果体。
     * @param snapshotFace 拓扑面快照。
     * @param fallbackPoint 回退点。
     * @returns 世界坐标代表点。
     */
    private buildWorldRepresentativePoint(
        owner: FeatureResultMesh,
        snapshotFace: TopologySnapshotFace | null,
        fallbackPoint: THREE.Vector3,
    ): THREE.Vector3 {
        if (!snapshotFace) {
            return fallbackPoint.clone();
        }
        return new THREE.Vector3(
            snapshotFace.localPoint[0],
            snapshotFace.localPoint[1],
            snapshotFace.localPoint[2],
        ).applyMatrix4(owner.matrixWorld);
    }

    /**
     * 构造世界坐标法向。
     *
     * @param owner 所属结果体。
     * @param face 面代理。
     * @param snapshotFace 拓扑面快照。
     * @returns 世界坐标法向。
     */
    private buildWorldPlaneNormal(
        owner: FeatureResultMesh,
        face: FeatureFace,
        snapshotFace: TopologySnapshotFace | null,
    ): THREE.Vector3 {
        const normalMatrix = new THREE.Matrix3().getNormalMatrix(owner.matrixWorld);
        if (snapshotFace) {
            return new THREE.Vector3(
                snapshotFace.localNormal[0],
                snapshotFace.localNormal[1],
                snapshotFace.localNormal[2],
            ).applyMatrix3(normalMatrix).normalize();
        }

        const fallbackNormal = this.buildFirstTriangleWorldNormal(face);
        if (fallbackNormal) {
            return fallbackNormal;
        }

        return new THREE.Vector3(0, 0, 1);
    }

    /**
     * 判断面是否为平面面。
     *
     * 设计说明：
     * 1. 新数据必须以 Puppy CAD 内核 拓扑曲面类型为准，避免细分网格噪声影响草图入口；
     * 2. 旧项目或导入链路可能没有 `surfaceKind`，此时保留网格法向一致性兜底；
     * 3. 非平面拓扑即使局部三角片共面，也不能被允许进入 2D 草图。
     *
     * @param face 面代理。
     * @param snapshotFace 拓扑面快照。
     * @returns 是否为平面面。
     */
    private isPlanarFace(face: FeatureFace, snapshotFace: TopologySnapshotFace | null): boolean {
        //------ 拓扑平面面判定 开始------
        const topologyPlanarity = this.resolveTopologyPlanarity(snapshotFace);
        if (topologyPlanarity === SketchTopologyPlanarity.Planar) {
            return true;
        }
        if (topologyPlanarity === SketchTopologyPlanarity.NonPlanar) {
            return false;
        }
        //------ 拓扑平面面判定 结束------

        const geometry = face.geometry;
        const positionAttribute = geometry.getAttribute('position');
        if (!(positionAttribute instanceof THREE.BufferAttribute)) {
            return false;
        }

        const indexAttribute = geometry.getIndex();
        const normalMatrix = new THREE.Matrix3().getNormalMatrix(face.matrixWorld);
        let referenceNormal: THREE.Vector3 | null = null;
        const triangleCount = indexAttribute instanceof THREE.BufferAttribute
            ? Math.floor(indexAttribute.count / 3)
            : Math.floor(positionAttribute.count / 3);

        for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
            const triangleNormal = this.buildTriangleWorldNormal(
                positionAttribute,
                indexAttribute,
                triangleIndex,
                normalMatrix,
            );
            if (!triangleNormal) {
                continue;
            }
            if (!referenceNormal) {
                referenceNormal = triangleNormal;
                continue;
            }
            if (Math.abs(referenceNormal.dot(triangleNormal)) < SketchPlanarFaceResolver.PLANAR_DOT_THRESHOLD) {
                return false;
            }
        }

        return referenceNormal !== null;
    }

    //------ 拓扑平面面判定 开始------
    /**
     * 从拓扑快照解析平面判定结果。
     *
     * 设计说明：
     * 1. `surfaceKind` 来自 worker 侧 Puppy CAD 内核 `BRepAdaptor_Surface.GetType()`，比渲染三角网格更接近 CAD 语义；
     * 2. 只有字段缺失才返回 `Unknown`，保证历史数据仍可按旧逻辑打开；
     * 3. 除 `plane` 外的所有已知曲面类型都按非平面处理，保持 2D 草图入口边界清晰。
     *
     * @param snapshotFace 拓扑面快照。
     * @returns 拓扑平面判定结果。
     */
    private resolveTopologyPlanarity(snapshotFace: TopologySnapshotFace | null): SketchTopologyPlanarity {
        const surfaceKind = snapshotFace?.surfaceKind;
        if (typeof surfaceKind !== 'string') {
            return SketchTopologyPlanarity.Unknown;
        }
        if (surfaceKind === 'plane') {
            return SketchTopologyPlanarity.Planar;
        }
        return SketchTopologyPlanarity.NonPlanar;
    }
    //------ 拓扑平面面判定 结束------

    /**
     * 读取首个非退化三角形法向。
     *
     * @param face 面代理。
     * @returns 世界坐标法向。
     */
    private buildFirstTriangleWorldNormal(face: FeatureFace): THREE.Vector3 | null {
        const geometry = face.geometry;
        const positionAttribute = geometry.getAttribute('position');
        if (!(positionAttribute instanceof THREE.BufferAttribute)) {
            return null;
        }

        const indexAttribute = geometry.getIndex();
        const normalMatrix = new THREE.Matrix3().getNormalMatrix(face.matrixWorld);
        const triangleCount = indexAttribute instanceof THREE.BufferAttribute
            ? Math.floor(indexAttribute.count / 3)
            : Math.floor(positionAttribute.count / 3);

        for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
            const normal = this.buildTriangleWorldNormal(
                positionAttribute,
                indexAttribute,
                triangleIndex,
                normalMatrix,
            );
            if (normal) {
                return normal;
            }
        }

        return null;
    }

    /**
     * 构造指定三角形的世界坐标法向。
     *
     * @param positionAttribute 顶点属性。
     * @param indexAttribute 索引属性。
     * @param triangleIndex 三角形索引。
     * @param normalMatrix 法向矩阵。
     * @returns 世界坐标法向；退化时返回 null。
     */
    private buildTriangleWorldNormal(
        positionAttribute: THREE.BufferAttribute,
        indexAttribute: THREE.BufferAttribute | null,
        triangleIndex: number,
        normalMatrix: THREE.Matrix3,
    ): THREE.Vector3 | null {
        const rawIndex0 = indexAttribute instanceof THREE.BufferAttribute
            ? indexAttribute.getX(triangleIndex * 3)
            : triangleIndex * 3;
        const rawIndex1 = indexAttribute instanceof THREE.BufferAttribute
            ? indexAttribute.getX(triangleIndex * 3 + 1)
            : triangleIndex * 3 + 1;
        const rawIndex2 = indexAttribute instanceof THREE.BufferAttribute
            ? indexAttribute.getX(triangleIndex * 3 + 2)
            : triangleIndex * 3 + 2;

        const point0 = new THREE.Vector3(
            positionAttribute.getX(rawIndex0),
            positionAttribute.getY(rawIndex0),
            positionAttribute.getZ(rawIndex0),
        );
        const point1 = new THREE.Vector3(
            positionAttribute.getX(rawIndex1),
            positionAttribute.getY(rawIndex1),
            positionAttribute.getZ(rawIndex1),
        );
        const point2 = new THREE.Vector3(
            positionAttribute.getX(rawIndex2),
            positionAttribute.getY(rawIndex2),
            positionAttribute.getZ(rawIndex2),
        );

        const normal = point1
            .clone()
            .sub(point0)
            .cross(point2.clone().sub(point0));
        if (normal.lengthSq() < 1e-12) {
            return null;
        }

        return normal.applyMatrix3(normalMatrix).normalize();
    }
}
